import { computed, type MaybeRefOrGetter, toValue, watch } from 'vue'

import { refDebounced } from '@vueuse/core'

import { useCoursesQueryStore } from '../stores/courses.query-store'
import { useLearningUIStore } from '../stores/learning.ui-store'
import { COURSE_TAG_FILTER_DEBOUNCE_MS } from '../constants/courses-filtering'

import type {
  AddCourseToGroupInput,
  CourseSortByValue,
  CreateCourseInput,
  ICourseProduct,
  SortDirectionValue,
  UpdateGroupCourseInput,
} from '../types'

export const useLearning = (groupId: MaybeRefOrGetter<string | undefined>, locationId: MaybeRefOrGetter<string | undefined>) => {
  const queryStore = useCoursesQueryStore(groupId, locationId)
  const uiStore = useLearningUIStore()

  // Selected tag ids are ObjectIds scoped to a single group's catalog. The learning UI store is a
  // singleton, so navigating from group A → group B (a same-route param change on
  // `/communities/groups/[groupSlug]/learning` — the component instance is reused and
  // `onUnmounted` does not fire) would carry group A's tag ids into group B's query, producing a
  // false empty state. Reset selections at the composable boundary so every consumer
  // (LearningHome, LearningHome.mobile, CoursesFilterSidebarIndex, …) inherits the fix.
  // `flush: 'pre'` clears the store before the queryKey recomputes, avoiding a wasted round-trip.
  watch(
    () => toValue(groupId),
    (nextGroupId, prevGroupId) => {
      if (prevGroupId === undefined || nextGroupId === prevGroupId) return
      uiStore.clearSelectedTagIds()
      uiStore.resetServerSort()
    },
    { flush: 'pre' }
  )

  // ===== QUERY METHODS =====

  const getGroupCourses = () => {
    return queryStore.createGroupCoursesQuery()
  }

  /**
   * Paginated group courses (20 per page by default) via the v2 API contract (admin/portal group view
   * and logged-out / guest viewers). Returns the underlying `useInfiniteQuery` alongside `allData`
   * (flattened list) and `total` (server total). The courses page should consume this instead of
   * `getGroupCourses` so it can fetch pages lazily.
   *
   * Filter/sort values default to the same UI store bindings used by `fetchUserCoursesInfiniteQuery`
   * so guests see the same server-side filtering + sorting as logged-in learners (the group-courses
   * endpoint accepts the identical `tagIds` / `sortBy` / `sort` contract). Debounced tag ids at
   * ~250ms keep rapid checkbox toggles from firing a request per click. Explicit overrides on the
   * `options` argument short-circuit the UI store defaults per field.
   */
  const fetchGroupCoursesInfiniteQuery = (options?: {
    limit?: MaybeRefOrGetter<number>
    tagIds?: MaybeRefOrGetter<readonly string[] | undefined>
    sortBy?: MaybeRefOrGetter<CourseSortByValue | undefined>
    sort?: MaybeRefOrGetter<SortDirectionValue | undefined>
    /** Caller-side gate — `LearningHome` uses this to restrict the query to guests only. */
    enabled?: MaybeRefOrGetter<boolean>
  }) => {
    const uiSelectedTagIds = computed(() => uiStore.selectedTagIds as readonly string[])
    const debouncedTagIds = refDebounced(uiSelectedTagIds, COURSE_TAG_FILTER_DEBOUNCE_MS)

    return queryStore.fetchGroupCoursesInfiniteQuery({
      limit: options?.limit,
      tagIds: options?.tagIds ?? (() => debouncedTagIds.value),
      sortBy: options?.sortBy ?? (() => uiStore.serverSortBy),
      sort: options?.sort ?? (() => uiStore.serverSort),
      enabled: options?.enabled,
    })
  }

  const getUserCourses = () => {
    return queryStore.createUserCoursesQuery()
  }

  /**
   * Paginated user courses (20 per page by default) via the v2 API contract, with server-side
   * tag filtering and sort. Returns the underlying `useInfiniteQuery` alongside `allData` (flattened
   * list) and `total` (server total). The courses page should consume this instead of
   * `getUserCourses` so it can fetch pages lazily.
   *
   * By default the filter/sort values are read reactively from the UI store, so callers just
   * mount the query once and any subsequent `uiStore.setSelectedTagIds(...)` / `setServerSort(...)`
   * change triggers a fresh page-1 fetch via TanStack's queryKey invalidation. Debouncing the tag
   * ids at ~250ms keeps rapid checkbox toggles from firing a request per click.
   *
   * Explicit overrides on the `options` argument (e.g. from Playwright tests or an ad-hoc caller)
   * short-circuit the UI store defaults for that particular field.
   */
  const fetchUserCoursesInfiniteQuery = (options?: {
    limit?: MaybeRefOrGetter<number>
    tagIds?: MaybeRefOrGetter<readonly string[] | undefined>
    sortBy?: MaybeRefOrGetter<CourseSortByValue | undefined>
    sort?: MaybeRefOrGetter<SortDirectionValue | undefined>
    /** Caller-side gate — `LearningHome` uses this to restrict the query to logged-in learners. */
    enabled?: MaybeRefOrGetter<boolean>
  }) => {
    const uiSelectedTagIds = computed(() => uiStore.selectedTagIds as readonly string[])
    const debouncedTagIds = refDebounced(uiSelectedTagIds, COURSE_TAG_FILTER_DEBOUNCE_MS)

    return queryStore.fetchUserCoursesInfiniteQuery({
      limit: options?.limit,
      tagIds: options?.tagIds ?? (() => debouncedTagIds.value),
      sortBy: options?.sortBy ?? (() => uiStore.serverSortBy),
      sort: options?.sort ?? (() => uiStore.serverSort),
      enabled: options?.enabled,
    })
  }

  /**
   * Fetch the deduped catalog of course tags for the current group.
   * Consumed by the filter panel to build category-grouped checkbox sections.
   */
  const fetchCourseTagsQuery = () => {
    return queryStore.fetchCourseTagsQuery()
  }

  const getCourseDetail = (courseId: MaybeRefOrGetter<string>) => {
    return queryStore.createCourseDetailQuery(courseId)
  }

  const getCoursesSuggestions = (searchQuery: MaybeRefOrGetter<string>) => {
    return queryStore.createCoursesSuggestionsQuery(searchQuery)
  }

  /**
   * Get all location-based courses (courses available in the location that can be added to groups)
   */
  const getLocationCourses = () => {
    return queryStore.createLocationCoursesQuery()
  }

  /**
   * Fetch the addable pool of location-group courses for the current group (admin-only,
   * unpaginated). Backed by `/{locationId}/groups/{groupId}/candidate-courses`. The backend
   * already excludes courses attached to the group, so this drives the Add Course dropdown
   * directly — the paginated user/group course queries can't be used because they only
   * surface loaded pages. Caller must gate `enabled` on admin status
   * (e.g. `() => props.isGroupAdmin`).
   */
  const fetchAddableCoursesQuery = (options?: { enabled?: MaybeRefOrGetter<boolean> }) => {
    return queryStore.fetchAddableCoursesQuery(options)
  }

  const getAccessibleGroupsForCourse = async (productId: string) => {
    return queryStore.fetchAccessibleGroupsForCourse(productId)
  }

  // ===== MUTATION METHODS =====

  const createCourse = async (data: CreateCourseInput) => {
    return queryStore.createCourseMutation.mutateAsync(data)
  }

  const updateCourse = async (courseId: string, data: UpdateGroupCourseInput) => {
    return queryStore.updateCourseMutation.mutateAsync({ courseId, data })
  }

  const deleteCourse = async (courseId: string) => {
    return queryStore.deleteCourseMutation.mutateAsync(courseId)
  }

  const reorderCourses = async (productId: string, previousProductId?: string, nextProductId?: string) => {
    const groupIdValue = toValue(groupId)
    const locationIdValue = toValue(locationId)
    if (!groupIdValue || !locationIdValue) {
      throw new Error('Group ID and Location ID are required')
    }
    return queryStore.reorderCoursesMutation.mutateAsync({
      groupId: groupIdValue,
      locationId: locationIdValue,
      productId,
      previousProductId,
      nextProductId,
    })
  }

  /**
   * Add a course to the current group
   */
  const addCourseToGroup = async (input: AddCourseToGroupInput) => {
    return queryStore.addCourseToGroupMutation.mutateAsync(input)
  }

  /**
   * Purchase a course (community-scoped) for the current user.
   * Uses the composable's groupId/locationId so callers only pass the offerId.
   */
  const purchaseCourse = async (offerId: string) => {
    return queryStore.purchaseCourseMutation.mutateAsync({ offerId })
  }

  // ===== BUSINESS LOGIC =====

  const getFilteredCourses = (courses: ICourseProduct[]) => {
    return computed(() => {
      let filtered = [...courses]

      // Search filter
      if (uiStore.searchQuery) {
        const query = uiStore.searchQuery.toLowerCase()
        filtered = filtered.filter(
          course => course.title.toLowerCase().includes(query) || course.description?.toLowerCase().includes(query)
        )
      }

      // Membership type filter
      if (uiStore.selectedMembershipType !== 'all') {
        filtered = filtered.filter(course => {
          if (uiStore.selectedMembershipType === 'free') {
            return course.membership?.membershipType === 'FREE' || !course.membership
          }
          if (uiStore.selectedMembershipType === 'paid') {
            return course.membership?.membershipType === 'PAID'
          }
          return true
        })
      }

      // Progress filter
      if (uiStore.selectedProgress !== 'all') {
        filtered = filtered.filter(course => {
          const progress = course.progress || 0
          if (uiStore.selectedProgress === 'not-started') {
            return progress === 0
          }
          if (uiStore.selectedProgress === 'in-progress') {
            return progress > 0 && progress < 100
          }
          if (uiStore.selectedProgress === 'completed') {
            return progress === 100
          }
          return true
        })
      }

      return filtered
    })
  }

  const getSortedCourses = (courses: ICourseProduct[]) => {
    return computed(() => {
      const sorted = [...courses]

      switch (uiStore.sortOrder) {
        case 'newest':
          return sorted.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0).getTime()
            const dateB = new Date(b.createdAt || 0).getTime()
            return dateB - dateA
          })
        case 'oldest':
          return sorted.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0).getTime()
            const dateB = new Date(b.createdAt || 0).getTime()
            return dateA - dateB
          })
        case 'alphabetical':
          return sorted.sort((a, b) => a.title.localeCompare(b.title))
        default:
          return sorted
      }
    })
  }

  const getCoursesWithAddCard = (courses: ICourseProduct[], isGroupAdmin: boolean, isMobile: boolean) => {
    return computed(() => {
      if (isGroupAdmin && !isMobile) {
        // Add empty object for add course card
        return [{}, ...courses]
      }
      return courses
    })
  }

  // ===== DRAG & DROP HELPERS =====

  const handleDragStart = (index: number) => {
    uiStore.startDrag(index)
  }

  const handleDragEnd = () => {
    uiStore.endDrag()
  }

  const handleCourseReorder = async (courses: ICourseProduct[], oldIndex: number, newIndex: number, _isGroupAdmin: boolean) => {
    if (oldIndex === newIndex) return

    // `courses` is expected to be the already-reordered list (as provided by vuedraggable's v-model update).
    const movedCourse = courses[newIndex]
    const productId = movedCourse?.productId || movedCourse?.id
    if (!productId) return

    const previousProductId = newIndex > 0 ? courses[newIndex - 1]?.productId || courses[newIndex - 1]?.id : undefined
    const nextProductId = newIndex < courses.length - 1 ? courses[newIndex + 1]?.productId || courses[newIndex + 1]?.id : undefined

    try {
      await reorderCourses(productId, previousProductId, nextProductId)
    } catch (error) {
      console.error('Failed to reorder courses:', error)
      // Could add toast notification here
    }
  }

  // ===== COURSE ACTIONS =====

  const openCourseDetails = (course: ICourseProduct) => {
    uiStore.openCourseDetails(course)
  }

  const closeCourseDetails = () => {
    uiStore.closeCourseDetails()
  }

  const openAddCourseModal = () => {
    uiStore.openAddCourseModal()
  }

  const closeAddCourseModal = () => {
    uiStore.closeAddCourseModal()
  }

  const openEditCourseModal = (course: ICourseProduct) => {
    uiStore.openEditCourseModal(course)
  }

  const closeEditCourseModal = () => {
    uiStore.closeEditCourseModal()
  }

  const openDeleteConfirmation = (course: ICourseProduct) => {
    uiStore.openDeleteConfirmation(course)
  }

  const closeDeleteConfirmation = () => {
    uiStore.closeDeleteConfirmation()
  }

  // ===== FILTER + SERVER-SORT PASSTHROUGHS =====
  // Named wrappers around the learning-ui store so components consume the composable and never
  // reach into the Pinia store directly (data-flow-pattern rule).

  const selectedTagIds = computed(() => uiStore.selectedTagIds)
  const serverSortBy = computed(() => uiStore.serverSortBy)
  const serverSort = computed(() => uiStore.serverSort)
  const hasActiveTagFilters = computed(() => uiStore.hasActiveTagFilters)
  const hasActiveServerSort = computed(() => uiStore.hasActiveServerSort)

  const toggleTagId = (id: string) => {
    uiStore.toggleTagId(id)
  }

  const setSelectedTagIds = (ids: readonly string[]) => {
    uiStore.setSelectedTagIds(ids)
  }

  const clearSelectedTagIds = () => {
    uiStore.clearSelectedTagIds()
  }

  const setServerSort = (sortBy: CourseSortByValue, sort: SortDirectionValue) => {
    uiStore.setServerSort(sortBy, sort)
  }

  const resetServerSort = () => {
    uiStore.resetServerSort()
  }

  // Composite "clear everything the filter rail owns" — used by CoursesFilterSidebar's clear-all
  // button, LearningHome's `hasActiveTagFilters` empty-state, and the 400-error reset watcher.
  const clearAllFilters = () => {
    uiStore.clearSelectedTagIds()
    uiStore.resetServerSort()
  }

  /**
   * Replace the selected ids that belong to a single category while preserving committed ids
   * from every other category. Consumers pass the category's full tag-id set (`categoryTagIds`)
   * plus the replacement selection for that category (`nextIdsInCategory`); the composable
   * strips the category's slice from the current bag and merges the replacement back in.
   *
   * Lives on the composable (not the store) because the store treats `selectedTagIds` as
   * category-agnostic — the "which ids belong to this category" split is a presentational
   * concern owned by the mobile category drawer.
   */
  const replaceCategoryTagSelection = (categoryTagIds: readonly string[], nextIdsInCategory: readonly string[]) => {
    const categoryIdSet = new Set(categoryTagIds)
    const outsideIds = uiStore.selectedTagIds.filter((id: string) => !categoryIdSet.has(id))
    uiStore.setSelectedTagIds([...outsideIds, ...nextIdsInCategory])
  }

  // ===== PUBLIC API =====

  return {
    // Query methods
    getGroupCourses,
    fetchGroupCoursesInfiniteQuery,
    getUserCourses,
    fetchUserCoursesInfiniteQuery,
    fetchCourseTagsQuery,
    getCourseDetail,
    getCoursesSuggestions,
    getLocationCourses,
    fetchAddableCoursesQuery,
    getAccessibleGroupsForCourse,

    // Mutation methods
    createCourse,
    updateCourse,
    deleteCourse,
    reorderCourses,
    addCourseToGroup,
    purchaseCourse,

    // Business logic
    getFilteredCourses,
    getSortedCourses,
    getCoursesWithAddCard,

    // Drag & drop
    handleDragStart,
    handleDragEnd,
    handleCourseReorder,

    // Course actions
    openCourseDetails,
    closeCourseDetails,
    openAddCourseModal,
    closeAddCourseModal,
    openEditCourseModal,
    closeEditCourseModal,
    openDeleteConfirmation,
    closeDeleteConfirmation,

    // Filter + server-sort state (read via composable, not uiStore, in new consumers)
    selectedTagIds,
    serverSortBy,
    serverSort,
    hasActiveTagFilters,
    hasActiveServerSort,
    toggleTagId,
    setSelectedTagIds,
    clearSelectedTagIds,
    setServerSort,
    resetServerSort,
    clearAllFilters,
    replaceCategoryTagSelection,

    // UI state access - expose uiStore directly to preserve reactivity
    uiStore,
  }
}
